import React, { useEffect } from 'react';
import { User, BookOpen, Clock, X, UserCheck, UserX } from 'lucide-react';
import FormInput from '../FormInput';
import useEnseignantData from '../../hooks/useEnseignantData';

const PonctualiteForm = ({
  showModal,
  setShowModal,
  activeTab,
  formData,
  setFormData,
  selectedClasse,
  setSelectedClasse,
  selectedModule,
  setSelectedModule,
  classes,
  etudiants,
  enseignants,
  modules,
  handleSubmit,
  resetForm,
  isEditing = false
}) => {
  // Hook pour gérer les données spécifiques aux enseignants
  const {
    classesEnseignant,
    modulesEnseignant,
    loadingClasses,
    loadingModules,
    fetchClassesByEnseignant,
    fetchModulesByEnseignantClasse,
    resetEnseignantData
  } = useEnseignantData();

  // Effet pour charger les classes quand un enseignant est sélectionné
  useEffect(() => {
    if (activeTab === 'enseignants' && formData.enseignant) {
      fetchClassesByEnseignant(formData.enseignant);
    } else if (activeTab === 'enseignants' && !formData.enseignant) {
      resetEnseignantData();
      if (!isEditing) {
        // En création, réinitialiser les sélections
        setSelectedClasse('');
        setSelectedModule('');
      }
    }
  }, [activeTab, formData.enseignant, fetchClassesByEnseignant, resetEnseignantData, setSelectedClasse, setSelectedModule, isEditing]);

  // Effet pour charger les modules quand une classe est sélectionnée pour un enseignant
  useEffect(() => {
    if (activeTab === 'enseignants' && formData.enseignant && selectedClasse) {
      fetchModulesByEnseignantClasse(formData.enseignant, selectedClasse);
    } else if (activeTab === 'enseignants' && !selectedClasse && !isEditing) {
      // En création, réinitialiser les modules si pas de classe
      setSelectedModule('');
    }
  }, [activeTab, formData.enseignant, selectedClasse, fetchModulesByEnseignantClasse, setSelectedModule, isEditing]);

  // Fonction utilitaire pour construire les options
  const buildOptions = (items, valueKey = 'id', labelKey = 'libelle', defaultLabel = 'Sélectionner...') => {
    const options = [{ value: '', label: defaultLabel }];
    
    // Ajouter toutes les options disponibles
    options.push(...items.map(item => ({
      value: item[valueKey],
      label: labelKey === 'nom_prenom' 
        ? `${item.nom} ${item.prenom}${item.matricule ? ` (${item.matricule})` : ''}`
        : item[labelKey]
    })));
    
    return options;
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header harmonisé */}
        <div className="bg-gradient-to-r from-[#202654] to-[#2a2f6c] px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                {activeTab === 'etudiants' ? (
                  <User className="h-5 w-5 text-white" />
                ) : (
                  <BookOpen className="h-5 w-5 text-white" />
                )}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold text-white">
                  {isEditing ? 'Modifier' : 'Nouvelle'} Absence
                </h3>
                  {isEditing && (
                    <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded-full font-medium">
                      Modification
                    </span>
                  )}
                </div>
                <p className="text-gray-200 text-sm">
                  {activeTab === 'etudiants' ? 'Étudiant' : 'Enseignant'}
                  {isEditing && ' - Tous les champs peuvent être modifiés'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Formulaire simplifié */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pour les étudiants: Classe en premier */}
              {activeTab === 'etudiants' && (
                <div className="md:col-span-2">
                  <FormInput
                    label="Classe"
                    type="select"
                    name="classe"
                    value={selectedClasse}
                    onChange={(e) => {
                      setSelectedClasse(e.target.value);
                    setSelectedModule('');
                  }}
                  options={buildOptions(classes, 'id', 'libelle', 'Sélectionner une classe')}
                    required
                    icon={BookOpen}
                  />
                </div>
              )}

            {/* Personne */}
              <div className="md:col-span-2">
                <FormInput
                  label={activeTab === 'etudiants' ? 'Étudiant' : 'Enseignant'}
                  type="select"
                  name={activeTab === 'etudiants' ? 'etudiant' : 'enseignant'}
                  value={activeTab === 'etudiants' ? formData.etudiant : formData.enseignant}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      [activeTab === 'etudiants' ? 'etudiant' : 'enseignant']: e.target.value
                    });
                  // Pour les enseignants, reset classe et module quand on change de personne
                  if (activeTab === 'enseignants') {
                    setSelectedClasse('');
                    setSelectedModule('');
                  }
                }}
                options={buildOptions(
                  activeTab === 'etudiants' ? etudiants : enseignants,
                  'id', 
                  'nom_prenom',
                  `Choisir ${activeTab === 'etudiants' ? 'un étudiant' : 'un enseignant'}`
                )}
                  required
                  icon={User}
                />
              </div>

              {/* Classe - Pour les enseignants seulement */}
              {activeTab === 'enseignants' && (
                <div className="md:col-span-2">
                  <FormInput
                    label="Classe"
                    type="select"
                    name="classe"
                    value={selectedClasse}
                    onChange={(e) => {
                      setSelectedClasse(e.target.value);
                      setSelectedModule(''); // Reset module quand on change de classe
                    }}
                  options={buildOptions(classesEnseignant, 'id', 'libelle', 'Sélectionner une classe')}
                  disabled={!isEditing && (!formData.enseignant || loadingClasses)}
                  required
                    icon={BookOpen}
                  helperText={
                    !formData.enseignant 
                      ? "Sélectionnez d'abord un enseignant"
                      : loadingClasses
                      ? "Chargement des classes..."
                      : undefined
                  }
                  />
                </div>
              )}

            {/* Date et Volume horaire sur la même ligne */}
            <div>
                <FormInput
                label="Date d'absence"
                type="date"
                name="date_absence"
                value={formData.date_absence || new Date().toISOString().split('T')[0]}
                onChange={(e) => setFormData({ ...formData, date_absence: e.target.value })}
                required
                />
              </div>

              <div>
                <FormInput
                label="Volume Horaire (h)"
                  type="number"
                  name="volume_horaire"
                  value={formData.volume_horaire}
                  onChange={(e) => setFormData({ ...formData, volume_horaire: e.target.value })}
                  required
                  min="1"
                  max="8"
                  placeholder="Ex: 2"
                  icon={Clock}
                />
              </div>

            {/* Module */}
            <div className="md:col-span-2">
              <FormInput
                label="Module"
                type="select"
                name="module"
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                options={buildOptions(
                  activeTab === 'etudiants' ? modules : modulesEnseignant,
                  'id',
                  'libelle',
                  'Sélectionner un module'
                )}
                disabled={
                  !isEditing && activeTab === 'enseignants' && 
                  (!formData.enseignant || !selectedClasse || loadingModules)
                }
                required
                icon={BookOpen}
                helperText={
                  activeTab === 'enseignants' && !formData.enseignant
                    ? "Sélectionnez d'abord un enseignant"
                    : activeTab === 'enseignants' && !selectedClasse
                    ? "Sélectionnez une classe"
                    : activeTab === 'enseignants' && loadingModules
                    ? "Chargement des modules..."
                    : activeTab === 'etudiants' && !selectedClasse
                    ? "Sélectionnez une classe pour voir les modules"
                    : undefined
                }
              />
              </div>

              {/* Motif */}
              <div className="md:col-span-2">
                <FormInput
                label="Motif (optionnel)"
                  type="textarea"
                  name="motif"
                  value={formData.motif}
                  onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                placeholder="Raison de l'absence..."
                rows={2}
                />
            </div>
          </div>

          {/* Boutons harmonisés */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 transition-colors"
            >
              {isEditing ? 'Modifier' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PonctualiteForm; 